import { EAuthErrorCode } from './auth-error-codes';

/**
 * Auth0's error strings, mapped onto the provider-neutral taxonomy.
 *
 * Two tables, not one, because the two inputs are matched differently today and
 * unifying them would change which errors count as non-recoverable:
 * SDK errors are matched by case-sensitive substring, whereas error *codes*
 * arriving from a callback URL or sessionStorage are matched by
 * case-insensitive prefix.
 */

/**
 * Lift of the SDK error patterns the token manager treats as non-recoverable.
 *
 * Order is load-bearing: an Auth0 message can contain more than one pattern,
 * and the first match wins. `Missing Refresh Token` must outrank the rest
 * (it is rate-tracked rather than silenced) and the silent session-lifecycle
 * patterns must outrank `invalid_grant` (which is reported to Sentry).
 */
const AUTH0_SDK_ERROR_PATTERNS: readonly (readonly [string, EAuthErrorCode])[] =
  [
    ['Missing Refresh Token', EAuthErrorCode.MISSING_REFRESH_TOKEN],
    ['login_required', EAuthErrorCode.LOGIN_REQUIRED],
    ['consent_required', EAuthErrorCode.CONSENT_REQUIRED],
    ['access_denied', EAuthErrorCode.ACCESS_DENIED],
    ['invalid_grant', EAuthErrorCode.INVALID_GRANT],
  ];

/**
 * Lift of the access-denial prefixes. The Auth0 SDK sets `error.message` to the
 * `error_description` value rather than the code, so both the code form
 * (`access_denied`) and the description form (`access denied...`) appear here.
 *
 * Only access denial is listed because that is the only distinction the
 * callback and stored-error paths draw today.
 */
const AUTH0_ERROR_CODE_PREFIXES: readonly (readonly [
  string,
  EAuthErrorCode,
])[] = [
  ['access_denied', EAuthErrorCode.ACCESS_DENIED],
  ['access denied', EAuthErrorCode.ACCESS_DENIED],
  ['unauthorized', EAuthErrorCode.ACCESS_DENIED],
];

/**
 * Maps an error thrown by the Auth0 SDK onto the neutral taxonomy.
 *
 * @param {unknown} error - Value thrown by the Auth0 SDK
 * @returns {EAuthErrorCode} The matching code, or `UNKNOWN` for anything that
 * is not an `Error` or matches no pattern
 */
export const mapAuth0SdkErrorToCode = (error: unknown): EAuthErrorCode => {
  if (!(error instanceof Error)) return EAuthErrorCode.UNKNOWN;

  const match = AUTH0_SDK_ERROR_PATTERNS.find(([pattern]) =>
    error.message.includes(pattern),
  );

  return match ? match[1] : EAuthErrorCode.UNKNOWN;
};

/**
 * Maps an Auth0 error code or description string onto the neutral taxonomy.
 *
 * Case-insensitive but whitespace-sensitive: callers that accept user- or
 * provider-supplied strings trim first.
 *
 * @param {string} errorCode - Error code or description, e.g. from a callback
 * URL's `error` param or a stored auth error
 * @returns {EAuthErrorCode} The matching code, or `UNKNOWN` when no prefix matches
 */
export const mapAuth0ErrorCodeToCode = (errorCode: string): EAuthErrorCode => {
  const normalized = errorCode.toLowerCase();

  const match = AUTH0_ERROR_CODE_PREFIXES.find(([prefix]) =>
    normalized.startsWith(prefix),
  );

  return match ? match[1] : EAuthErrorCode.UNKNOWN;
};
